import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Form, Button, Alert, Accordion, Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { workStagesAPI } from '../../services/workStagesApi'

const workStageSchema = yup.object({
  name: yup.string().required('Название обязательно'),
  foreman_discount_percentage: yup.number()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .min(0, 'Минимум 0%')
    .max(100, 'Максимум 100%'),
}).required()

const workSchema = yup.object({
  name: yup.string().required('Наименование обязательно'),
  unit: yup.string().required('Единица измерения обязательна'),
  base_price: yup.number().required('Базовая цена обязательна').min(0, 'Цена не может быть отрицательной'),
  asr: yup.boolean(),
  exclude_from_smeta_m: yup.boolean(),
}).required()

const WorkStageEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [workStage, setWorkStage] = useState(null)
  const [works, setWorks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingWork, setEditingWork] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Form for work stage name
  const {
    register: registerStage,
    handleSubmit: handleSubmitStage,
    formState: { errors: stageErrors },
    reset: resetStage,
  } = useForm({
    resolver: yupResolver(workStageSchema),
  })

  // Form for adding new work
  const {
    register: registerWork,
    handleSubmit: handleSubmitWork,
    formState: { errors: workErrors },
    reset: resetWork,
  } = useForm({
    resolver: yupResolver(workSchema),
    defaultValues: {
      asr: false,
      exclude_from_smeta_m: false,
    },
  })

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [stageResponse, worksResponse] = await Promise.all([
        workStagesAPI.get(id),
        workStagesAPI.getWorks(id),
      ])
      setWorkStage(stageResponse.data)
      setWorks(worksResponse.data)
      resetStage({
        name: stageResponse.data.name,
        foreman_discount_percentage: stageResponse.data.foreman_discount_percentage || '',
      })
    } catch (err) {
      setError('Ошибка загрузки данных')
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const onSubmitStage = async (data) => {
    try {
      setError(null)
      await workStagesAPI.update(id, data)
      setWorkStage({ ...workStage, name: data.name })
    } catch (err) {
      setError('Ошибка при обновлении этапа работ')
      console.error('Error updating work stage:', err)
    }
  }

  const onSubmitWork = async (data) => {
    try {
      setError(null)
      await workStagesAPI.createWork(id, data)
      setShowAddModal(false)
      resetWork({
        name: '',
        unit: '',
        base_price: '',
        asr: false,
        exclude_from_smeta_m: false,
      })
      await loadData()
    } catch (err) {
      setError('Ошибка при добавлении работы')
      console.error('Error creating work:', err)
    }
  }

  const handleUpdateWork = async (workId, data) => {
    try {
      setError(null)
      await workStagesAPI.updateWork(id, workId, data)
      await loadData()
    } catch (err) {
      setError('Ошибка при обновлении работы')
      console.error('Error updating work:', err)
    }
  }

  const handleReorderWorks = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = works.findIndex((w) => w.id === active.id)
    const newIndex = works.findIndex((w) => w.id === over.id)
    const newWorks = arrayMove(works, oldIndex, newIndex)
    setWorks(newWorks)

    try {
      const ids = newWorks.map((w) => w.id)
      await workStagesAPI.reorderWorks(id, ids)
    } catch (err) {
      console.error('Error reordering works:', err)
      setWorks(works)
      setError('Ошибка при изменении порядка работ')
    }
  }

  const handleDeleteWork = async (workId, workName) => {
    if (!window.confirm(`Удалить работу "${workName}"?`)) {
      return
    }

    try {
      setError(null)
      await workStagesAPI.deleteWork(id, workId)
      setWorks(works.filter(w => w.id !== workId))
    } catch (err) {
      setError('Ошибка при удалении работы')
      console.error('Error deleting work:', err)
    }
  }

  if (loading) {
    return <div>Загрузка...</div>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Редактирование этапа работ</h1>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Work Stage Name Form */}
      <Form onSubmit={handleSubmitStage(onSubmitStage)} className="mb-4">
        <Form.Group className="mb-3">
          <Form.Label>Название</Form.Label>
          <Form.Control
            type="text"
            {...registerStage('name')}
            isInvalid={!!stageErrors.name}
          />
          <Form.Control.Feedback type="invalid">
            {stageErrors.name?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Стоимость работы мастера (%)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="Введите процент (0-100) или оставьте пустым"
            {...registerStage('foreman_discount_percentage')}
            isInvalid={!!stageErrors.foreman_discount_percentage}
          />
          <Form.Control.Feedback type="invalid">
            {stageErrors.foreman_discount_percentage?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Процент стоимости работы мастера для этого этапа. Можно оставить пустым (будет 0%).
          </Form.Text>
        </Form.Group>

        <Button type="submit" variant="primary" size="sm">
          Сохранить
        </Button>
      </Form>

      {/* Works Section */}
      <div className="work-stage-works-section mb-4">
        <div className="work-stage-works-header d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">Работы этапа</h5>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAddModal(true)}
          >
            Добавить работу
          </Button>
        </div>

        {works.length > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorderWorks}>
            <SortableContext items={works.map((w) => w.id)} strategy={verticalListSortingStrategy}>
              <Accordion>
                {works.map((work) => (
                  <WorkItem
                    key={work.id}
                    work={work}
                    workStageId={id}
                    onUpdate={handleUpdateWork}
                    onDelete={handleDeleteWork}
                  />
                ))}
              </Accordion>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-muted mb-0">
            Работы пока не добавлены. Нажмите «Добавить работу», чтобы создать первую запись.
          </p>
        )}
      </div>

      <Link to="/work_stages" className="btn btn-secondary btn-sm">
        Назад к списку
      </Link>

      {/* Add Work Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Новая работа этапа</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitWork(onSubmitWork)}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Наименование</Form.Label>
              <Form.Control
                type="text"
                {...registerWork('name')}
                isInvalid={!!workErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {workErrors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Единица измерения</Form.Label>
              <Form.Control
                type="text"
                {...registerWork('unit')}
                isInvalid={!!workErrors.unit}
              />
              <Form.Control.Feedback type="invalid">
                {workErrors.unit?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Базовая цена, руб.</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                {...registerWork('base_price')}
                isInvalid={!!workErrors.base_price}
              />
              <Form.Control.Feedback type="invalid">
                {workErrors.base_price?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="АСР (акт скрытых работ)"
                {...registerWork('asr')}
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Check
                type="checkbox"
                label="Не отображать в смете-М"
                {...registerWork('exclude_from_smeta_m')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="primary">
              Добавить
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

// Separate component for each work item with its own form
const WorkItem = ({ work, workStageId, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: work.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(workSchema),
    defaultValues: {
      name: work.name,
      unit: work.unit,
      base_price: work.base_price,
      asr: work.asr || false,
      exclude_from_smeta_m: work.exclude_from_smeta_m || false,
    },
  })

  const onSubmit = (data) => {
    onUpdate(work.id, data)
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price || 0)
  }

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
    <Accordion.Item eventKey={work.id.toString()} className="rounded-3">
      <div className="d-flex align-items-center">
        <div
          {...attributes}
          {...listeners}
          className="px-2"
          style={{ cursor: 'move', touchAction: 'none', flexShrink: 0 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </div>
        <Accordion.Header className="flex-grow-1 bg-white py-3 shadow-none">
          <div className="d-flex flex-column flex-lg-row w-100 justify-content-between align-items-lg-center gap-3">
            <div className="d-flex flex-column">
              <span className="fw-semibold">{work.name}</span>
              <span className="text-muted small">
                Ед. изм.: {work.unit || '—'}
              </span>
            </div>
            <span className="badge bg-light text-dark fw-semibold px-3 py-2">
              {formatPrice(work.base_price)}
            </span>
          </div>
        </Accordion.Header>
      </div>
      <Accordion.Body className="bg-light">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="row gy-3 gx-4 align-items-end">
            <div className="col-lg-4 col-md-6">
              <Form.Label className="fw-semibold small mb-1">
                Наименование
              </Form.Label>
              <Form.Control
                type="text"
                {...register('name')}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </div>

            <div className="col-lg-3 col-md-3">
              <Form.Label className="fw-semibold small mb-1">
                Единица измерения
              </Form.Label>
              <Form.Control
                type="text"
                {...register('unit')}
                isInvalid={!!errors.unit}
              />
              <Form.Control.Feedback type="invalid">
                {errors.unit?.message}
              </Form.Control.Feedback>
            </div>

            <div className="col-lg-3 col-md-3">
              <Form.Label className="fw-semibold small mb-1">
                Базовая цена, руб.
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                {...register('base_price')}
                isInvalid={!!errors.base_price}
              />
              <Form.Control.Feedback type="invalid">
                {errors.base_price?.message}
              </Form.Control.Feedback>
            </div>

            <div className="col-lg-2 col-md-12">
              <div className="form-check mt-4">
                <Form.Check
                  type="checkbox"
                  label="АСР"
                  {...register('asr')}
                />
              </div>
              <div className="form-check mt-2">
                <Form.Check
                  type="checkbox"
                  label="Не отображать в смете-М"
                  {...register('exclude_from_smeta_m')}
                />
              </div>
            </div>

            <div className="col-12 d-flex flex-wrap gap-2 justify-content-lg-end">
              <Button type="submit" variant="primary" size="sm">
                Сохранить
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => onDelete(work.id, work.name)}
              >
                Удалить
              </Button>
            </div>
          </div>
        </Form>
      </Accordion.Body>
    </Accordion.Item>
    </div>
  )
}

export default WorkStageEdit
